import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { createApiCall } from 'services';

type CompanyStylesResponse = {
  id: string;
  styles: Theme;
  /**
   * VRG-309 — present only for an acquisition demo company.
   *
   * ⚠️ ENRICHMENT, NOT THE TRIGGER. Whether the demo banner renders is decided by
   * the hostname (`utils/subdomain.ts` `isDemoHost`), never by this field. The API
   * lookup behind it is best-effort and swallows its own failures, so keying the
   * disclosure off it would fail OPEN — serving a prospect's scraped brand assets
   * with nothing saying Vera made the page. This only supplies nicer copy.
   */
  demo?: {
    brandName?: string;
  };
};

const getCompanyBySubdomain = async (subdomain: string): Promise<CompanyStylesResponse> => {
  const response = await createApiCall({
    method: 'GET',
    url: `/companies/public/by-subdomain/${subdomain}`,
  });

  // Check if response indicates an error (errorHandler returns error.response)
  if (response.status && response.status >= 400) {
    throw new Error(response.data?.message || 'Company not found');
  }

  return response.data as CompanyStylesResponse;
};

export const useGetCompanyBySubdomain = (subdomain: string | null): UseQueryResult<CompanyStylesResponse, Error> => {
  return useQuery({
    queryKey: ['companyBySubdomain', subdomain],
    queryFn: () => getCompanyBySubdomain(subdomain!),
    enabled: !!subdomain, // Only run query if subdomain exists
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1, // Only retry once if failed
  });
};
